'use client';

import { useState, useEffect, useCallback, useRef, useMemo, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import api from '@/lib/api';
import { useScratchpadApplyTarget } from '@/lib/scratchpad';
import { aiModelsApi } from '@/lib/ai-models-api';
import { JobResponse, HistoryItem, QueueStats, HealthResponse, JobCreate, BatchJobCreate, Series, PermutationList, PermutationListItem } from '@/lib/types';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmDialog';
import GenerationForm from '@/components/GenerationForm';
import JobCard from '@/components/JobCard';
import HistoryPanel from '@/components/HistoryPanel';
import StatusBar from '@/components/StatusBar';
import ImageModal from '@/components/ImageModal';
import FloatingWindow from '@/components/FloatingWindow';
import CollageAssistantPanel from '@/components/collage/CollageAssistantPanel';
import ImagePromptArea from '@/components/ImagePromptArea';
import SeriesPanel from '@/components/SeriesPanel';
import UnifiedListModal from '@/components/UnifiedListModal';

// Filter out comment lines (starting with --) from prompt, matching backend behavior
function filterPromptComments(prompt: string): string {
  if (!prompt) return prompt;
  return prompt
    .split('\n')
    .filter(line => !line.trimStart().startsWith('--'))
    .join('\n');
}

// Debounce helper
function debounce<T extends (...args: Parameters<T>) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

interface CollageListCardProps {
  list: PermutationList;
  matchedItemValue: string | null;
  selectedIndex: number;
  isFocused: boolean;
  isCollapsed: boolean;
  onSelect: (index: number) => void;
  onOpenList: () => void;
  onToggleCollapse: () => void;
  onRemove: () => void;
}

function CollageListCard({
  list,
  matchedItemValue,
  selectedIndex,
  isFocused,
  isCollapsed,
  onSelect,
  onOpenList,
  onToggleCollapse,
  onRemove,
}: CollageListCardProps) {
  const activeItems = list.items.filter(i => i.is_active);

  // Get 5 items: -2, -1, current, +1, +2
  const visibleIndices = [
    selectedIndex - 2,
    selectedIndex - 1,
    selectedIndex,
    selectedIndex + 1,
    selectedIndex + 2,
  ];

  const renderThumbnail = (index: number, position: 'far' | 'near' | 'center') => {
    const item = activeItems[index];
    const isValid = index >= 0 && index < activeItems.length;

    // Size based on position
    const sizeClass = position === 'center' ? 'w-14 h-14' : position === 'near' ? 'w-11 h-11' : 'w-9 h-9';
    const opacityClass = position === 'center' ? 'opacity-100' : position === 'near' ? 'opacity-70' : 'opacity-40';
    const ringClass = position === 'center' ? 'ring-2 ring-[var(--accent)]' : '';

    if (!isValid || !item) {
      return (
        <div
          className={`${sizeClass} rounded-lg bg-[var(--bg-secondary)] flex-shrink-0`}
        />
      );
    }

    const content = item.preview_image ? (
      <img
        src={api.getImageUrl(item.preview_image)}
        alt={item.value}
        className={`${sizeClass} rounded-lg object-cover ${opacityClass} ${ringClass} flex-shrink-0`}
      />
    ) : (
      (() => {
        const letter = item.value.charAt(0).toUpperCase();
        const colors = ['bg-blue-500', 'bg-purple-500', 'bg-pink-500', 'bg-orange-500', 'bg-teal-500'];
        const colorIndex = item.value.length % colors.length;
        return (
          <div
            className={`${sizeClass} rounded-lg ${colors[colorIndex]} flex items-center justify-center text-white font-bold text-xs ${opacityClass} ${ringClass} flex-shrink-0`}
          >
            {position !== 'far' ? letter : ''}
          </div>
        );
      })()
    );

    return (
      <button
        onClick={() => onSelect(index)}
        className="cursor-pointer hover:scale-105 transition-transform focus:outline-none"
        title={item.value}
      >
        {content}
      </button>
    );
  };

  return (
    <div
      className={`rounded-xl border transition-all ${
        isFocused
          ? 'border-[var(--accent)] bg-[var(--accent-soft)]'
          : matchedItemValue
            ? 'border-green-500/50 bg-green-500/5'
            : 'border-[var(--border-subtle)] bg-[var(--bg-elevated)]'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border-subtle)]">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={onOpenList}
            className="text-xs font-medium truncate text-left hover:text-[var(--accent)] hover:underline transition-colors cursor-pointer"
            title={`Open "${list.name}" in the list manager`}
          >
            {list.name}
          </button>
          {matchedItemValue && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 truncate max-w-[80px]">
              matched
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          <button
            onClick={onToggleCollapse}
            className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            <svg className={`w-3 h-3 transition-transform ${isCollapsed ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button
            onClick={onRemove}
            className="p-1 text-[var(--text-muted)] hover:text-[var(--error)] transition-colors"
            title="Remove list"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="p-2 space-y-1.5">
          {/* 5-item carousel */}
          <div className="flex items-center justify-center gap-1">
            <button
              onClick={() => onSelect(Math.max(0, selectedIndex - 1))}
              disabled={selectedIndex === 0}
              className="p-0.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-30 transition-colors flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex items-center justify-center gap-1">
              {renderThumbnail(visibleIndices[0], 'far')}
              {renderThumbnail(visibleIndices[1], 'near')}
              {renderThumbnail(visibleIndices[2], 'center')}
              {renderThumbnail(visibleIndices[3], 'near')}
              {renderThumbnail(visibleIndices[4], 'far')}
            </div>

            <button
              onClick={() => onSelect(Math.min(activeItems.length - 1, selectedIndex + 1))}
              disabled={selectedIndex >= activeItems.length - 1}
              className="p-0.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-30 transition-colors flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Current item value */}
          <p className="text-center text-[10px] text-[var(--text-muted)] truncate px-1">
            {activeItems[selectedIndex]?.value || 'No items'}
          </p>

          {/* Position indicator */}
          <div className="text-center text-[10px] text-[var(--text-muted)]">
            {selectedIndex + 1} / {activeItems.length}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CollagePage() {
  return (
    <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
      <CollageContent />
    </Suspense>
  );
}

function CollageContent() {
  const searchParams = useSearchParams();

  // Data state (same as Create page)
  const [jobs, setJobs] = useState<JobResponse[]>([]);
  const jobsRef = useRef<JobResponse[]>([]);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState<Partial<JobCreate>>({});
  const [series, setSeries] = useState<Series[]>([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState<number | null>(null);
  const [urlParamsApplied, setUrlParamsApplied] = useState(false);
  const toast = useToast();
  const { confirm } = useConfirm();

  // Collage-specific state
  const [lists, setLists] = useState<PermutationList[]>([]);
  const [activeLists, setActiveLists] = useState<number[]>([]); // List IDs in display order
  const [selectedItems, setSelectedItems] = useState<Map<number, number>>(new Map()); // listId → itemIndex
  const [focusedListIndex, setFocusedListIndex] = useState(0);
  const [collapsedLists, setCollapsedLists] = useState<Set<number>>(new Set());
  // List whose full manager dialog is open, opened by clicking its navigator label
  const [listModalListId, setListModalListId] = useState<number | null>(null);

  // Controlled prompt state for CollageNavigator integration
  const [prompt, setPrompt] = useState('');

  // Offer this prompt field to the scratchpad's Apply action.
  useScratchpadApplyTarget(setPrompt);

  // Dimensions and seed from GenerationForm (for preview)
  const [dimensions, setDimensions] = useState({ width: 1024, height: 1024 });
  const [previewSeed, setPreviewSeed] = useState<number | null>(null);

  // Memoized callbacks to prevent infinite loop
  const handleDimensionsChange = useCallback((w: number, h: number) => {
    setDimensions(prev => {
      if (prev.width === w && prev.height === h) return prev;
      return { width: w, height: h };
    });
  }, []);

  const [quality, setQuality] = useState({ steps: 9, cfg: 1.0 });
  const handleQualityChange = useCallback((steps: number, cfg: number) => {
    setQuality(prev => (prev.steps === steps && prev.cfg === cfg ? prev : { steps, cfg }));
  }, []);

  const handleSeedChange = useCallback((seed: number | null) => {
    setPreviewSeed(prev => prev === seed ? prev : seed);
  }, []);

  // Seed stepper beside Generate. Writes both halves the way set_settings does:
  // previewSeed drives the live preview, formValues.seed reaches the generation
  // form. Seed is kept out of the form's remount key, so stepping it syncs through
  // GenerationForm's initialValues effect instead of remounting the form and
  // discarding whatever the user had set by hand in Advanced settings.
  const setSeedValue = useCallback((next: number | null) => {
    setPreviewSeed(next);
    // Drop prompt on the way through. GenerationForm re-applies initialValues.prompt
    // every time initialValues changes, and prompt is controlled by this page, so
    // leaving the one "Use Settings" wrote in there would push a stale prompt back
    // over whatever the navigator has stepped to since.
    setFormValues(prev => {
      const { prompt: _ignored, ...rest } = prev;
      return { ...rest, seed: next };
    });
  }, []);

  const bumpSeed = useCallback((delta: number) => {
    // No seed yet means the backend rolls a random one; the first step commits to
    // an explicit value so that stepping is reproducible from there on.
    setSeedValue(Math.min(2147483647, Math.max(0, (previewSeed ?? 0) + delta)));
  }, [previewSeed, setSeedValue]);

  // Preview state - single image reused across navigator, floating window, and modal
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  // The prompt the on-screen preview was generated from. Kept separate from the live prompt,
  // which keeps changing as lists are stepped through while the image lags a request behind.
  const [previewPrompt, setPreviewPrompt] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewEnabled, setPreviewEnabled] = useState(true);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewDimensions, setPreviewDimensions] = useState({ width: 512, height: 512 });

  const [assistantOpen, setAssistantOpen] = useState(false);

  // Geometry now lives in FloatingWindow; only the open flag belongs to the page
  const [floatingPreviewOpen, setFloatingPreviewOpen] = useState(false);

  // Throttle refs for preview generation
  const lastGenerationTimeRef = useRef(0);
  const pendingGenerationRef = useRef<{ prompt: string; width: number; height: number; seed: number | null } | null>(null);
  const throttleTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-detect lists from prompt text and update activeLists
  useEffect(() => {
    if (!prompt || lists.length === 0) return;

    // Find all lists that have a matching item in the prompt
    const detectedListIds: number[] = [];
    const detectedMatches = new Map<number, { value: string; index: number }>();

    for (const list of lists) {
      const activeItems = list.items.filter(i => i.is_active);
      for (let i = 0; i < activeItems.length; i++) {
        const item = activeItems[i];
        if (item.value && prompt.includes(item.value)) {
          detectedListIds.push(list.id);
          detectedMatches.set(list.id, { value: item.value, index: i });
          break; // Only first match per list
        }
      }
    }

    // Update activeLists: keep existing order, add new detected lists at the end
    setActiveLists(prev => {
      const existingWithMatches = prev.filter(id => detectedListIds.includes(id));
      const newLists = detectedListIds.filter(id => !prev.includes(id));

      // Keep existing lists that still have matches, add new ones
      if (newLists.length > 0 || existingWithMatches.length !== prev.filter(id => detectedListIds.includes(id)).length) {
        return [...existingWithMatches, ...newLists];
      }
      return prev;
    });

    // Update selectedItems for detected matches
    setSelectedItems(prev => {
      const newSelected = new Map(prev);
      let changed = false;

      for (const [listId, match] of detectedMatches) {
        if (newSelected.get(listId) !== match.index) {
          newSelected.set(listId, match.index);
          changed = true;
        }
      }

      return changed ? newSelected : prev;
    });
  }, [prompt, lists]);

  // Find which list items are matched in the current prompt (for display)
  const matchedItems = useMemo(() => {
    const matches = new Map<number, string>(); // listId → matched value
    if (!prompt) return matches;

    for (const listId of activeLists) {
      const list = lists.find(l => l.id === listId);
      if (!list) continue;

      const activeItems = list.items.filter(i => i.is_active);
      for (const item of activeItems) {
        if (item.value && prompt.includes(item.value)) {
          matches.set(listId, item.value);
          break; // Only first match per list
        }
      }
    }
    return matches;
  }, [prompt, activeLists, lists]);

  // Handle item selection in CollageNavigator - replace text in prompt
  const handleItemSelect = useCallback((listId: number, newIndex: number) => {
    const list = lists.find(l => l.id === listId);
    if (!list) return;

    const activeItems = list.items.filter(i => i.is_active);
    const oldIndex = selectedItems.get(listId) ?? 0;
    const oldItem = activeItems[oldIndex];
    const newItem = activeItems[newIndex];

    if (!newItem) return;

    // Update selected items
    setSelectedItems(prev => new Map(prev).set(listId, newIndex));

    // Replace text in prompt
    if (oldItem && prompt.includes(oldItem.value)) {
      setPrompt(prompt.replace(oldItem.value, newItem.value));
    } else if (!prompt.includes(newItem.value)) {
      // If old item not in prompt, append new item on new line
      setPrompt(prev => prev ? `${prev}\n${newItem.value}` : newItem.value);
    }
  }, [lists, selectedItems, prompt]);

  // "Use This" in the list dialog: routed through handleItemSelect, the same path the
  // navigator carousel uses, so only the matched span in the prompt is swapped.
  const handleModalItemSelect = useCallback((listId: number, value: string) => {
    const list = lists.find(l => l.id === listId);
    const index = list
      ? list.items.filter(i => i.is_active).findIndex(i => i.value === value)
      : -1;

    if (index >= 0) {
      if (!activeLists.includes(listId)) setActiveLists(prev => [...prev, listId]);
      handleItemSelect(listId, index);
    } else {
      // A value the navigator cannot map back to an active item (deactivated, edited,
      // or a multi-value {a|b} insert) — append it as plain text.
      setPrompt(prev => (prev ? `${prev}\n${value}` : value));
    }
  }, [lists, activeLists, handleItemSelect]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [healthRes, statsRes, jobsRes, historyRes, seriesRes, listsRes] = await Promise.all([
        api.getHealth(),
        api.getQueueStats(),
        api.listJobs(undefined, 500, 0, selectedSeriesId ?? undefined),
        api.getHistory(50),
        api.getSeries(),
        api.getPermutationLists(),
      ]);
      setHealth(healthRes);
      setStats(statsRes);
      setJobs(jobsRes);
      jobsRef.current = jobsRes;
      setHistory(historyRes);
      setSeries(seriesRes);
      setLists(listsRes);

      // Auto-select first 3 lists if none selected yet
      if (activeLists.length === 0 && listsRes.length > 0) {
        const defaultLists = listsRes
          .filter(l => l.items.some(i => i.is_active))
          .slice(0, 3)
          .map(l => l.id);
        setActiveLists(defaultLists);

        // Initialize selected items to first item in each list
        const initialSelected = new Map<number, number>();
        defaultLists.forEach(listId => initialSelected.set(listId, 0));
        setSelectedItems(initialSelected);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [selectedSeriesId, activeLists.length]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      const hasPendingOrProcessing = jobsRef.current.some(
        j => j.status === 'pending' || j.status === 'processing'
      );
      if (hasPendingOrProcessing) {
        fetchData();
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Apply URL parameters on mount (for "Use in Collage" from variants page)
  useEffect(() => {
    if (urlParamsApplied) return;

    const promptParam = searchParams.get('prompt');
    const seriesIdParam = searchParams.get('series_id');
    const widthParam = searchParams.get('width');
    const heightParam = searchParams.get('height');
    const stepsParam = searchParams.get('steps');
    const cfgParam = searchParams.get('cfg');
    const seedParam = searchParams.get('seed');

    if (promptParam) {
      setPrompt(decodeURIComponent(promptParam));

      const newFormValues: Partial<JobCreate> = {
        prompt: decodeURIComponent(promptParam),
      };

      if (widthParam) newFormValues.width = parseInt(widthParam);
      if (heightParam) newFormValues.height = parseInt(heightParam);
      if (stepsParam) newFormValues.steps = parseInt(stepsParam);
      if (cfgParam) newFormValues.cfg = parseFloat(cfgParam);
      if (seedParam && seedParam !== 'null') newFormValues.seed = parseInt(seedParam);
      if (seriesIdParam) newFormValues.series_id = parseInt(seriesIdParam);

      setFormValues(newFormValues);

      if (seriesIdParam) {
        setSelectedSeriesId(parseInt(seriesIdParam));
      }

      setUrlParamsApplied(true);
      toast.success('Prompt loaded from Variants page');
    }
  }, [searchParams, urlParamsApplied, toast]);

  // Debounced preview generation with correct aspect ratio and seed
  const generatePreview = useCallback(async (promptText: string, width: number, height: number, seed: number | null) => {
    if (!promptText.trim() || !previewEnabled) return;

    setPreviewLoading(true);

    try {
      const baseSize = 512;
      const aspectRatio = width / height;
      let previewWidth: number;
      let previewHeight: number;

      // Round to nearest multiple of 16
      const roundTo16 = (n: number) => Math.round(n / 16) * 16;

      if (aspectRatio >= 1) {
        // Landscape or square: height is the smaller dimension
        previewHeight = baseSize;
        previewWidth = Math.max(baseSize, roundTo16(baseSize * aspectRatio));
      } else {
        // Portrait: width is the smaller dimension
        previewWidth = baseSize;
        previewHeight = Math.max(baseSize, roundTo16(baseSize / aspectRatio));
      }

      const result = await aiModelsApi.imagegenGenerate({
        prompt: filterPromptComments(promptText),
        width: previewWidth,
        height: previewHeight,
        num_inference_steps: 4,
        seed: seed,
        model: 'z-image-turbo',
      });

      setPreviewImage(result.image);
      setPreviewPrompt(promptText);
      setPreviewDimensions({ width: previewWidth, height: previewHeight });
      lastGenerationTimeRef.current = Date.now();
    } catch (error) {
      console.error('Preview generation failed:', error);
    } finally {
      setPreviewLoading(false);
    }
  }, [previewEnabled]);

  // Throttled generation: enforces minimum 2s gap when floating preview is open
  const throttledGenerate = useCallback((promptText: string, width: number, height: number, seed: number | null) => {
    if (!floatingPreviewOpen) {
      // No throttle when floating preview is closed
      generatePreview(promptText, width, height, seed);
      return;
    }

    const now = Date.now();
    const elapsed = now - lastGenerationTimeRef.current;
    const minGap = 2000;

    if (elapsed >= minGap) {
      // Enough time passed, generate immediately
      generatePreview(promptText, width, height, seed);
    } else {
      // Queue the latest params and schedule generation after cooldown
      pendingGenerationRef.current = { prompt: promptText, width, height, seed };
      if (!throttleTimerRef.current) {
        throttleTimerRef.current = setTimeout(() => {
          throttleTimerRef.current = null;
          const pending = pendingGenerationRef.current;
          if (pending) {
            pendingGenerationRef.current = null;
            generatePreview(pending.prompt, pending.width, pending.height, pending.seed);
          }
        }, minGap - elapsed);
      }
    }
  }, [floatingPreviewOpen, generatePreview]);

  // Clean up throttle timer
  useEffect(() => {
    return () => {
      if (throttleTimerRef.current) clearTimeout(throttleTimerRef.current);
    };
  }, []);

  const debouncedGeneratePreview = useMemo(
    () => debounce((promptText: string, width: number, height: number, seed: number | null) => throttledGenerate(promptText, width, height, seed), 800),
    [throttledGenerate]
  );

  useEffect(() => {
    if (prompt.trim() && previewEnabled) {
      debouncedGeneratePreview(prompt, dimensions.width, dimensions.height, previewSeed);
    }
  }, [prompt, dimensions, previewSeed, previewEnabled, debouncedGeneratePreview]);

  // Keyboard navigation for CollageNavigator
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if not in an input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      // Don't handle when image modal is open (it has its own keyboard handler)
      if (selectedJob) return;
      // Same for the list dialog: arrows there walk items, not navigator cards
      if (listModalListId !== null) return;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          setFocusedListIndex(prev => Math.max(0, prev - 1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusedListIndex(prev => Math.min(activeLists.length - 1, prev + 1));
          break;
        case 'ArrowLeft':
          e.preventDefault();
          {
            const focusedListId = activeLists[focusedListIndex];
            if (focusedListId !== undefined) {
              const currentIndex = selectedItems.get(focusedListId) ?? 0;
              handleItemSelect(focusedListId, Math.max(0, currentIndex - 1));
            }
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          {
            const focusedListId = activeLists[focusedListIndex];
            if (focusedListId !== undefined) {
              const list = lists.find(l => l.id === focusedListId);
              const activeItems = list?.items.filter(i => i.is_active) ?? [];
              const currentIndex = selectedItems.get(focusedListId) ?? 0;
              handleItemSelect(focusedListId, Math.min(activeItems.length - 1, currentIndex + 1));
            }
          }
          break;
        case ' ':
          e.preventDefault();
          {
            const focusedListId = activeLists[focusedListIndex];
            if (focusedListId !== undefined) {
              setCollapsedLists(prev => {
                const next = new Set(prev);
                if (next.has(focusedListId)) {
                  next.delete(focusedListId);
                } else {
                  next.add(focusedListId);
                }
                return next;
              });
            }
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeLists, focusedListIndex, lists, selectedItems, handleItemSelect, selectedJob, listModalListId]);

  // Handlers (same as Create page)
  const handleSubmit = async (jobData: JobCreate) => {
    setIsSubmitting(true);
    try {
      await api.createJob(jobData);
      await fetchData();
    } catch (error) {
      console.error('Failed to create job:', error);
      toast.error('Failed to create job: ' + (error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBatchSubmit = async (request: BatchJobCreate) => {
    const preview = await api.previewPermutations(request.prompt);
    if (preview.total_permutations > 20) {
      const confirmed = await confirm({
        title: 'Large Batch',
        message: `This will create ${preview.total_permutations} jobs. Continue?`,
        confirmText: 'Generate All',
        variant: 'warning',
      });
      if (!confirmed) return;
    }

    setIsSubmitting(true);
    try {
      const result = await api.generatePermutations(request);
      await fetchData();
      toast.success(`Created ${result.total_jobs} jobs from permutations`);
    } catch (error) {
      console.error('Failed to create batch jobs:', error);
      toast.error('Failed to create batch jobs: ' + (error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async (jobId: string, force: boolean = false) => {
    try {
      await api.cancelJob(jobId, force);
      await fetchData();
    } catch (error) {
      console.error('Failed to cancel job:', error);
      toast.error('Failed to cancel job: ' + (error as Error).message);
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    // Optimistically remove from local state immediately
    setJobs(prev => prev.filter(j => j.job_id !== jobId));
    try {
      await api.deleteJobPermanent(jobId);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete job:', error);
      await fetchData(); // Restore correct state on failure
    }
  };

  const handleDeleteHistoryItem = async (itemId: number) => {
    try {
      await api.deleteHistoryItem(itemId);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete history item:', error);
    }
  };

  const handleReuseFromHistory = (item: HistoryItem) => {
    setPrompt(item.prompt);
    setFormValues({
      prompt: item.prompt,
      width: item.params.width,
      height: item.params.height,
      steps: item.params.steps,
      cfg: item.params.cfg,
      seed: item.params.seed,
      series_id: item.params.series_id ?? undefined,
    });
    if (item.job_type === 'batch' && item.params.series_id) {
      setSelectedSeriesId(item.params.series_id);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReuseFromJob = (job: JobResponse) => {
    setPrompt(job.prompt);
    setFormValues({
      prompt: job.prompt,
      width: job.width,
      height: job.height,
      steps: job.steps,
      cfg: job.cfg,
      seed: job.seed,
    });
    setSelectedJob(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearHistory = async (period?: 'today' | 'yesterday' | 'all') => {
    const periodLabels = { today: "today's", yesterday: "yesterday's", all: 'all' };
    const label = periodLabels[period || 'all'];

    const confirmed = await confirm({
      title: 'Clear History',
      message: `This will permanently delete ${label} history items.`,
      confirmText: period === 'all' ? 'Clear All' : `Clear ${label.charAt(0).toUpperCase() + label.slice(1)}`,
      variant: 'danger',
    });

    if (confirmed) {
      try {
        const result = await api.clearHistoryByPeriod(period || 'all');
        await fetchData();
        toast.success(`Cleared ${result.cleared} history items`);
      } catch (error) {
        console.error('Failed to clear history:', error);
        toast.error('Failed to clear history');
      }
    }
  };

  const handleAddList = (listId: number) => {
    if (!activeLists.includes(listId)) {
      setActiveLists(prev => [...prev, listId]);
      setSelectedItems(prev => new Map(prev).set(listId, 0));
    }
  };

  const handleRemoveList = (listId: number) => {
    setActiveLists(prev => prev.filter(id => id !== listId));
    setSelectedItems(prev => {
      const next = new Map(prev);
      next.delete(listId);
      return next;
    });
    setFocusedListIndex(prev => Math.min(prev, activeLists.length - 2));
  };

  const activeJobs = jobs.filter(j => j.status === 'pending' || j.status === 'processing');
  const completedJobs = jobs.filter(j => j.status === 'completed');
  const availableLists = lists.filter(l => !activeLists.includes(l.id) && l.items.some(i => i.is_active));

  const totalImages = series.reduce((sum, s) => sum + s.image_count, 0) +
    jobs.filter(j => j.status === 'completed' && !j.series_id).length;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <StatusBar health={health} stats={stats} onRefresh={fetchData} />

      <div className="flex-1 flex overflow-hidden">
        {/* Series Sidebar (same as Create page) */}
        <aside className="hidden lg:block w-64 border-r border-[var(--border-subtle)] flex-shrink-0 overflow-y-auto">
          <SeriesPanel
            series={series}
            selectedSeriesId={selectedSeriesId}
            totalImages={totalImages}
            onSelectSeries={setSelectedSeriesId}
            onSeriesChange={fetchData}
          />
        </aside>

        {/* Collage Navigator Sidebar */}
        <aside className="hidden lg:flex lg:flex-col w-72 border-r border-[var(--border-subtle)] flex-shrink-0 bg-[var(--bg-secondary)]">
          {/* Fixed top section: Header + Preview */}
          <div className="flex-shrink-0 p-3 space-y-3 border-b border-[var(--border-subtle)]">
            <div className="flex items-center justify-between">
              <h2 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                Navigator
              </h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setAssistantOpen(prev => !prev)}
                  className={`p-1 rounded transition-colors ${assistantOpen ? 'text-[var(--accent)] bg-[var(--accent-soft)]' : 'text-[var(--text-muted)]'}`}
                  title={assistantOpen ? 'Close assistant' : 'Open assistant'}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 10.5h8M8 14h5m-9 6.5 3.2-2.4A2 2 0 0 1 8.4 17.7H18a2 2 0 0 0 2-2V6.5a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v14Z" />
                  </svg>
                </button>
                <button
                  onClick={() => setFloatingPreviewOpen(prev => !prev)}
                  className={`p-1 rounded transition-colors ${floatingPreviewOpen ? 'text-[var(--accent)] bg-[var(--accent-soft)]' : 'text-[var(--text-muted)]'}`}
                  title={floatingPreviewOpen ? 'Close floating preview' : 'Open floating preview'}
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <rect x="2" y="3" width="20" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                    <rect x="12" y="10" width="10" height="10" rx="1" fill="currentColor" opacity={floatingPreviewOpen ? 0.3 : 0} strokeLinecap="round" strokeLinejoin="round" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 10h10v10H12z" />
                  </svg>
                </button>
                <button
                  onClick={() => setPreviewEnabled(!previewEnabled)}
                  className={`p-1 rounded transition-colors ${previewEnabled ? 'text-[var(--accent)] bg-[var(--accent-soft)]' : 'text-[var(--text-muted)]'}`}
                  title={previewEnabled ? 'Disable preview' : 'Enable preview'}
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Preview thumbnail */}
            {previewEnabled && (
              <div
                className="relative rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] overflow-hidden"
                style={{ aspectRatio: `${dimensions.width} / ${dimensions.height}` }}
              >
                {previewLoading ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-5 h-5 animate-spin text-[var(--accent)]" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  </div>
                ) : previewImage ? (
                  <img
                    src={previewImage.startsWith('data:') ? previewImage : `data:image/png;base64,${previewImage}`}
                    alt="Preview"
                    className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => setPreviewModalOpen(true)}
                    title="Click to enlarge"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-[var(--text-muted)]">
                    <svg className="w-8 h-8 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Scrollable section: Lists + Add + Keyboard hints */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {/* Active Lists */}
            <div className="space-y-2">
              {activeLists.map((listId, index) => {
                const list = lists.find(l => l.id === listId);
                if (!list) return null;
                return (
                  <CollageListCard
                    key={listId}
                    list={list}
                    matchedItemValue={matchedItems.get(listId) || null}
                    selectedIndex={selectedItems.get(listId) ?? 0}
                    isFocused={index === focusedListIndex}
                    isCollapsed={collapsedLists.has(listId)}
                    onSelect={(idx) => handleItemSelect(listId, idx)}
                    onOpenList={() => setListModalListId(listId)}
                    onToggleCollapse={() => {
                      setCollapsedLists(prev => {
                        const next = new Set(prev);
                        if (next.has(listId)) next.delete(listId);
                        else next.add(listId);
                        return next;
                      });
                    }}
                    onRemove={() => handleRemoveList(listId)}
                  />
                );
              })}
            </div>

            {/* Add List */}
            {availableLists.length > 0 && (
              <select
                className="w-full px-2 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-xs focus:outline-none focus:ring-1 focus:ring-[var(--accent)]"
                value=""
                onChange={(e) => handleAddList(parseInt(e.target.value))}
              >
                <option value="">+ Add List...</option>
                {availableLists.map(list => (
                  <option key={list.id} value={list.id}>
                    {list.name} ({list.active_count})
                  </option>
                ))}
              </select>
            )}

            {/* Keyboard hints */}
            <div className="pt-2 border-t border-[var(--border-subtle)]">
              <p className="text-[10px] text-[var(--text-muted)] mb-1">Keyboard</p>
              <div className="grid grid-cols-2 gap-0.5 text-[10px] text-[var(--text-muted)]">
                <span className="font-mono bg-[var(--bg-primary)] px-1 rounded">↑↓</span>
                <span>List</span>
                <span className="font-mono bg-[var(--bg-primary)] px-1 rounded">←→</span>
                <span>Item</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content (same as Create page) */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-6 py-8 space-y-10">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <h1 className="text-3xl font-semibold tracking-tight">Collage</h1>
                <p className="text-[var(--text-muted)]">Navigate permutation lists and generate combinations</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {/* Seed stepper - the one advanced setting worth reaching without opening the panel */}
                <div className="flex items-center gap-1" title="Seed - leave empty for a random seed each run">
                  <button
                    type="button"
                    onClick={() => bumpSeed(-1)}
                    className="btn h-10 w-10 text-xl leading-none font-semibold shrink-0"
                    aria-label="Previous seed"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={previewSeed ?? ''}
                    onChange={(e) => {
                      const v = e.target.value.trim();
                      if (v === '') { setSeedValue(null); return; }
                      const n = parseInt(v, 10);
                      if (!isNaN(n)) setSeedValue(Math.min(2147483647, Math.max(0, n)));
                    }}
                    placeholder="Random"
                    className="input h-10 w-28 text-center"
                    aria-label="Seed"
                  />
                  <button
                    type="button"
                    onClick={() => bumpSeed(1)}
                    className="btn h-10 w-10 text-xl leading-none font-semibold shrink-0"
                    aria-label="Next seed"
                  >
                    +
                  </button>
                </div>
                <button
                  type="submit"
                  form="collage-gen-form"
                  disabled={isSubmitting || !prompt.trim()}
                  className="btn btn-primary h-10 px-5 font-semibold shrink-0"
                >
                  Generate
                </button>
              </div>
            </div>

            {/* Generation Form (with controlled prompt) */}
            <GenerationForm
              formId="collage-gen-form"
              // Seed and prompt are excluded. The stepper changes seed constantly and
              // GenerationForm already syncs it through its initialValues effect, so
              // remounting each step would throw away hand-set Advanced values. Prompt
              // is controlled from this page via promptValue, so it has no business
              // forcing a remount either.
              key={JSON.stringify({ ...formValues, seed: undefined, prompt: undefined })}
              onSubmit={handleSubmit}
              onBatchSubmit={handleBatchSubmit}
              isLoading={isSubmitting}
              initialValues={formValues}
              series={series}
              selectedSeriesId={selectedSeriesId}
              promptValue={prompt}
              onPromptChange={setPrompt}
              onDimensionsChange={handleDimensionsChange}
              onSeedChange={handleSeedChange}
              onQualityChange={handleQualityChange}
            />

            {/* Active Jobs */}
            {activeJobs.length > 0 && (
              <section className="space-y-4">
                <h2 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider">
                  In Progress
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {activeJobs.map((job) => (
                    <JobCard
                      key={job.job_id}
                      job={job}
                      onCancel={handleCancel}
                      onSelect={setSelectedJob}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Gallery */}
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider">
                  Gallery
                </h2>
                {completedJobs.length > 0 && (
                  <span className="text-xs text-[var(--text-muted)]">
                    {completedJobs.length} images
                  </span>
                )}
              </div>

              {completedJobs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 rounded-full bg-[var(--bg-elevated)] flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                    </svg>
                  </div>
                  <p className="text-[var(--text-muted)]">No images yet</p>
                  <p className="text-sm text-[var(--text-muted)] mt-1">Your creations will appear here</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {completedJobs.map((job) => (
                    <JobCard
                      key={job.job_id}
                      job={job}
                      onSelect={setSelectedJob}
                      onDelete={handleDeleteJob}
                      onCopy={handleReuseFromJob}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        </main>

        {/* History Sidebar (same as Create page) */}
        <aside className="hidden lg:block w-72 border-l border-[var(--border-subtle)] flex-shrink-0 overflow-hidden">
          <HistoryPanel
            history={history}
            onReuseParams={handleReuseFromHistory}
            onDeleteItem={handleDeleteHistoryItem}
            onClearHistory={handleClearHistory}
          />
        </aside>
      </div>

      {/* Floating Preview Window */}
      {previewEnabled && (
        <FloatingWindow
          title="Preview"
          open={floatingPreviewOpen}
          onClose={() => setFloatingPreviewOpen(false)}
          storageKey="collage-preview"
          defaultPosition="top-right"
          defaultWidth={512}
          defaultHeight={512}
          minWidth={200}
          /* Never wider than the image itself, as before the migration */
          maxWidth={previewDimensions.width}
          /* Both axes, locked to the image's ratio: the body takes its height from that
             ratio, so any frame shaped differently crops the image or pads it with dead
             space. Dragging the corner scales the whole thing. */
          resizeAxis="both"
          contentSize={previewDimensions}
          lockAspect
        >
          <div
            className="relative bg-[var(--bg-primary)] flex-1"
            style={{ aspectRatio: `${dimensions.width} / ${dimensions.height}` }}
          >
            {previewImage ? (
              <img
                src={previewImage.startsWith('data:') ? previewImage : `data:image/png;base64,${previewImage}`}
                alt="Preview"
                className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setPreviewModalOpen(true)}
                title="Click to enlarge"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-[var(--text-muted)]">
                <svg className="w-10 h-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
            {previewLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <svg className="w-6 h-6 animate-spin text-[var(--accent)]" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            )}
          </div>
        </FloatingWindow>
      )}

      {/* Preview Modal */}
      {previewModalOpen && previewImage && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-black/80 backdrop-blur-sm cursor-pointer"
          onClick={() => setPreviewModalOpen(false)}
        >
          {/* Same prompt area as the gallery viewer on Create / Variants. Clicks stay inside it
              so selecting prompt text doesn't dismiss the image behind it. */}
          <div
            className="flex items-center justify-center shrink-0 min-h-14 px-4 py-2 bg-black/50 cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <ImagePromptArea
              prompt={previewPrompt}
              onNavigate={() => setPreviewModalOpen(false)}
            />
          </div>
          <div className="flex-1 flex items-center justify-center min-h-0 p-4">
            <img
              src={previewImage.startsWith('data:') ? previewImage : `data:image/png;base64,${previewImage}`}
              alt={previewPrompt || 'Preview'}
              style={{
                width: previewDimensions.width,
                height: previewDimensions.height,
              }}
              className="rounded-lg shadow-2xl max-w-full max-h-full object-contain"
            />
          </div>
        </div>
      )}

      {/* AI assistant */}
      <FloatingWindow
        title="Assistant"
        open={assistantOpen}
        onClose={() => setAssistantOpen(false)}
        storageKey="collage-assistant"
        defaultPosition="bottom-right"
        defaultWidth={400}
        defaultHeight={520}
        minWidth={300}
      >
        <CollageAssistantPanel
          getPageState={() => ({
            prompt,
            active_lists: activeLists
              .map((id) => lists.find((l) => l.id === id)?.name)
              .filter((n): n is string => Boolean(n)),
            series_id: selectedSeriesId,
            series_name: series.find((s) => s.id === selectedSeriesId)?.name ?? null,
            width: dimensions.width,
            height: dimensions.height,
            seed: previewSeed,
            steps: quality.steps,
            cfg: quality.cfg,
            preview_open: floatingPreviewOpen && previewEnabled,
            selected_items: Object.fromEntries(
              activeLists.flatMap((id) => {
                const l = lists.find((x) => x.id === id);
                if (!l) return [];
                const active = l.items.filter((i) => i.is_active);
                const picked = active[selectedItems.get(id) ?? 0];
                return picked ? [[l.name, picked.value] as [string, string]] : [];
              })
            ),
          })}
          onCollageOps={(ops) => {
            // Applied here rather than in the panel, which stays ignorant of page
            // internals — the same split AgentChatPanel uses for build_ops.
            for (const op of ops) {
              if (op.op === 'set_prompt' && typeof op.text === 'string') {
                setPrompt(op.text);
              } else if (op.op === 'select_item') {
                // Routed through handleItemSelect, the same path the navigator uses, so only
                // the matched span in the prompt changes and the selection stays in sync.
                const pl = op.payload as {
                  list: string; value?: string; index?: number; direction?: 'next' | 'previous';
                };
                const l = lists.find((x) => x.name.toLowerCase() === pl.list.toLowerCase());
                if (l) {
                  const active = l.items.filter((i) => i.is_active);
                  const current = selectedItems.get(l.id) ?? 0;
                  let next = current;
                  if (pl.direction) {
                    next = pl.direction === 'next'
                      ? (current + 1) % active.length
                      : (current - 1 + active.length) % active.length;
                  } else if (typeof pl.value === 'string') {
                    const found = active.findIndex(
                      (i) => i.value.toLowerCase() === pl.value!.toLowerCase());
                    if (found >= 0) next = found;
                  } else if (typeof pl.index === 'number') {
                    next = Math.max(0, Math.min(pl.index, active.length - 1));
                  }
                  if (!activeLists.includes(l.id)) setActiveLists((prev) => [...prev, l.id]);
                  handleItemSelect(l.id, next);
                }
              } else if (op.op === 'set_preview') {
                setFloatingPreviewOpen(Boolean((op.payload as { visible?: boolean }).visible));
              } else if (op.op === 'set_settings') {
                // Pushed through formValues so the generation form remounts with them,
                // the same route "Use Settings" takes — otherwise the form's own copy
                // would keep overwriting the page's.
                const pl = op.payload as {
                  width?: number; height?: number; seed?: number | null;
                  steps?: number; cfg?: number; series?: string;
                };
                if (typeof pl.width === 'number' && typeof pl.height === 'number') {
                  setDimensions({ width: pl.width, height: pl.height });
                }
                if ('seed' in pl) setPreviewSeed(pl.seed ?? null);
                if (typeof (pl as { series?: string }).series === 'string') {
                  const wanted = (pl as { series?: string }).series!.trim().toLowerCase();
                  const match = series.find((x) => x.name.toLowerCase() === wanted);
                  if (match) setSelectedSeriesId(match.id);
                }
                setFormValues((prev) => ({
                  ...prev,
                  ...(typeof pl.width === 'number' ? { width: pl.width } : {}),
                  ...(typeof pl.height === 'number' ? { height: pl.height } : {}),
                  ...('seed' in pl ? { seed: pl.seed ?? null } : {}),
                  ...(typeof pl.steps === 'number' ? { steps: pl.steps } : {}),
                  ...(typeof pl.cfg === 'number' ? { cfg: pl.cfg } : {}),
                }));
              } else if (op.op === 'prefill_batch') {
                // Deliberately stops short of generating: the user presses Generate.
                const pl = op.payload as {
                  prompt?: string; series_id?: number; width?: number; height?: number;
                };
                if (typeof pl.prompt === 'string') setPrompt(pl.prompt);
                if (typeof pl.series_id === 'number') setSelectedSeriesId(pl.series_id);
                if (typeof pl.width === 'number' && typeof pl.height === 'number') {
                  setDimensions({ width: pl.width, height: pl.height });
                }
              } else if (op.op === 'activate_lists') {
                // The assistant names lists; the navigator is keyed by id
                const ids = op.names
                  .map((n) => lists.find((l) => l.name.toLowerCase() === n.toLowerCase())?.id)
                  .filter((id): id is number => typeof id === 'number');
                if (ids.length) {
                  setActiveLists((prev) => [...prev, ...ids.filter((id) => !prev.includes(id))]);
                }
              }
            }
          }}
        />
      </FloatingWindow>

      {/* Permutation List Dialog - opened from a navigator label */}
      {listModalListId !== null && (
        <UnifiedListModal
          initialMode="manager"
          initialListId={listModalListId}
          onSelect={(value) => handleModalItemSelect(listModalListId, value)}
          onInsertVariable={(listName) => setPrompt(prev => prev ? `${prev}\n{${listName}}` : `{${listName}}`)}
          onClose={() => {
            setListModalListId(null);
            // Items may have been added, edited, or deactivated in there
            fetchData();
          }}
        />
      )}

      {/* Image Modal */}
      {selectedJob && selectedJob.status === 'completed' && (
        <ImageModal
          job={selectedJob}
          jobs={completedJobs}
          onClose={() => setSelectedJob(null)}
          onReuseParams={handleReuseFromJob}
          onDelete={handleDeleteJob}
          onNavigate={setSelectedJob}
          series={series}
          onSeriesChange={fetchData}
        />
      )}
    </div>
  );
}
